import {Injectable} from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import {map, Observable, of} from "rxjs";
import {apiUrl} from "../../environments/environment";
import {CreateProjectRequest, Project} from "../_models/project";
import {Page} from "../_models/page";
import {ProjectTable, ProjectTableResponse} from "../_models/project-table";
import {FloorDuplicateMenu} from "../_models/floor";
import {SubRole, User} from "../_models/user";
import {ProjectProgressUserSettings} from "../_models/projectProgressUserSettings";
import {convertSubRoleToEnum, convertEffectiveRoleToEnum} from "../_util/utils";
import {Trade} from "../_models/trade";
import {SUPPRESS_DUPLICATE_NAME_NOTIFICATION} from "../_interceptors/error-interceptor.context";

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {

  constructor(private http: HttpClient) {
  }


  getProjectListByParams(companyId: number, hideArchived: boolean, companyName: string, clientName: string, query: string, sortField: string, sortDirection: string, page: number, pageSize: number, hideCompleted: boolean = false): Observable<Page<Project>> {
    let params = new HttpParams();
    if (companyId) params = params.set('companyId', companyId);
    if (companyName) params = params.set('companyName', companyName);
    if (hideArchived) params = params.set('hideArchived', hideArchived);
    if (hideCompleted) params = params.set('hideCompleted', hideCompleted);
    if (clientName) params = params.set('clientName', clientName);
    if (query && query.length > 0) params = params.set('query', query);
    if (sortField) params = params.set('sortField', sortField);
    if (sortDirection) params = params.set('sortDirection', sortDirection);
    if (page) params = params.set('page', page);
    if (pageSize) params = params.set('pageSize', pageSize);

    return this.http.get<Page<Project>>(`${apiUrl}/project/list`, {
      params: params,
      withCredentials: true
    })
  }

  getProjectShortList(companyId: number, excludeSubRole?: SubRole): Observable<Project[]> {
    let params = new HttpParams().set('companyId', companyId);
    if (excludeSubRole) params = params.set('excludeSubRole', excludeSubRole);

    return this.http.get<Project[]>(`${apiUrl}/project/short`, {params: params, withCredentials: true})
  }

  getProjectAccess(projectId: number, test?: string): Observable<SubRole> {
    return this.http.get<SubRole>(`${apiUrl}/project/${projectId}/access`, {withCredentials: true}).pipe(map(subRole => {
      return convertSubRoleToEnum(subRole)
    }))
  }

  export(companyId: number, hideArchived: boolean, companyName: string, clientName: string, query: string, sortField: string, sortDirection: string) {
    let params = new HttpParams();
    if (companyId) params = params.set('companyId', companyId);
    if (companyName) params = params.set('companyName', companyName);
    if (hideArchived) params = params.set('hideArchived', hideArchived);
    if (clientName) params = params.set('clientName', clientName);
    if (query && query.length > 0) params = params.set('query', query);
    if (sortField) params = params.set('sortField', sortField);
    if (sortDirection) params = params.set('sortDirection', sortDirection);

    return this.http.get(`${apiUrl}/project/export`, {
      params: params,
      withCredentials: true,
      responseType: 'blob',
      observe: 'response'
    })
  }

  getProjectById(projectId: number): Observable<Project> {
    return this.http.get<Project>(`${apiUrl}/project/${projectId}`, {withCredentials: true})
  }

  getProjectProgressById(projectId: number): Observable<number> {
    return this.http.get<number>(`${apiUrl}/project/${projectId}/progress`, {withCredentials: true})
  }

  createProject(companyId: number, project: Project): Observable<Project> {
    return this.http.post<Project>(`${apiUrl}/project`, this.toCreateRequest(project), {
      params: {companyId},
      context: new HttpContext().set(SUPPRESS_DUPLICATE_NAME_NOTIFICATION, true),
      withCredentials: true
    })
  }

  validateProject(companyId: number, project: Project) {
    return this.http.post(`${apiUrl}/project/validate`, this.toCreateRequest(project), {
      params: {companyId},
      context: new HttpContext().set(SUPPRESS_DUPLICATE_NAME_NOTIFICATION, true),
      withCredentials: true
    })
  }

  private toCreateRequest(project: Project): CreateProjectRequest {
    return {
      name: project.name,
      publicKey: project.publicKey,
      number: project.number,
      address1: project.address1,
      address2: project.address2,
      city: project.city,
      zipCode: project.zipCode,
      state: project.state,
      clientName: project.clientName,
      startDate: project.startDate,
      endDate: project.endDate,
      unitType: project.unitType,
      tradeType: project.tradeType,
      sections: project.sections || []
    };
  }

  createProjectKey(projectName: string): Observable<any> {
    return this.http.get(`${apiUrl}/project/key/generateKey`, {params: {name: projectName}, withCredentials: true})
  }

  validateProjectKey(projectCode: string): Observable<{error: string}> {
    let params = new HttpParams().append('projectKey', projectCode)
    return this.http.get<{error: string}>(`${apiUrl}/project/key/validate`, {withCredentials: true, params: params})
  }

  updateProject(project: Project): Observable<Project> {
    return this.http.put<Project>(`${apiUrl}/project`, project, {
      context: new HttpContext().set(SUPPRESS_DUPLICATE_NAME_NOTIFICATION, true),
      withCredentials: true
    })
  }

  archiveProject(projectId: number) {
    return this.http.put(`${apiUrl}/project/${projectId}/archive`, null, {withCredentials: true})
  }

  unArchiveProject(projectId: number) {
    return this.http.put(`${apiUrl}/project/${projectId}/unarchive`, null, {withCredentials: true})
  }

  deleteProjects(projectIds: number[]) {
    let params = new HttpParams();
    projectIds.forEach(e => params = params.append('projectIds', e));

    return this.http.delete<void>(`${apiUrl}/project`, {
      params: params,
      withCredentials: true
    })
  }

  assignEmployeesToProject(projectId: number, employeeIds: number[], subRole: SubRole) {
    return this.http.post(`${apiUrl}/project/${projectId}/employees`, employeeIds, {
      params: {subRole},
      observe: "response",
      withCredentials: true
    })
  }

  removeEmployeesFromProject(projectId: number, employeeIds: number[]) {
    return this.http.delete(`${apiUrl}/project/${projectId}/employees`, {
      params: {employeeIds: employeeIds},
      observe: "response",
      withCredentials: true
    }
    )
  }

  updateEmployeeProjectRole(projectId: number, employeeId: number, subRole: SubRole) {
    return this.http.put(`${apiUrl}/project/${projectId}/employee`, null, {
      params: {subRole, employeeId},
      observe: "response",
      withCredentials: true
    })
  }

  getProjectProgressTable(projectId: number, withEffDates: boolean = false, sectionId: number | null = null): Observable<ProjectTableResponse> {
    if (!sectionId) {
      return of({
        table: new ProjectTable(),
        currentSystemDate: null
      });
    }

    let params = new HttpParams().set('projectId', String(projectId));
    if (withEffDates) params = params.set('withEffDates', String(withEffDates));

    return this.http.get<ProjectTable>(`${apiUrl}/section/${sectionId}/table`, {
      params,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map((response) => ({
        table: response.body,
        currentSystemDate: response.headers.get('Date')
      }))
    )
  }

  updateRoomsOrderProjectProgressTable(projectId: number, sectionId: number, headMap: Map<string, number>, tableVersion: number) {
    return this.http.put<ProjectTable>(`${apiUrl}/section/${sectionId}/table/rooms/order`, Object.fromEntries(headMap), {
      params: {projectId, tableVersion},
      withCredentials: true
    })
  }

  updateAreasOrderProjectProgressTable(projectId: number, sectionId: number, headMap: Map<string, number[]>, tableVersion: number) {
    return this.http.put<ProjectTable>(`${apiUrl}/section/${sectionId}/table/areas/order`, Object.fromEntries(headMap), {
      params: {projectId, tableVersion},
      withCredentials: true
    })
  }

  getUnitsListInProject(projectId: number, sectionId: number, entityName?: string | null, phaseGroupId?: number | null) {
    let params = new HttpParams().set('sectionId', sectionId);

    if (entityName?.trim()) {
      params = params.set('entityName', entityName.trim());
    }

    if (phaseGroupId) {
      params = params.set('phaseGroupId', phaseGroupId);
    }

    return this.http.get<FloorDuplicateMenu[]>(`${apiUrl}/project/${projectId}/units/short`, {params, withCredentials: true})
  }

  getProjectMentionUsers(projectId: number, username: string, excludeViewers: boolean = false): Observable<User[]> {
    return this.http.get<User[]>(`${apiUrl}/project/${projectId}/employee/mention`, {params: {username, excludeViewers}, withCredentials: true})
  }

  getProjectProgressSettings(projectId: number) {
    return this.http.get<ProjectProgressUserSettings>(`${apiUrl}/project/${projectId}/projectProgressSettings`, {withCredentials: true})
  }

  updateProjectProgressSettings(projectId: number, userSettings: ProjectProgressUserSettings) {
    return this.http.put<ProjectProgressUserSettings>(`${apiUrl}/project/${projectId}/projectProgressSettings`, userSettings, {withCredentials: true})
  }

  useInheritedDates(id: number) {
    return this.http.put<void>(`${apiUrl}/project/${id}/useInheritedDates`, null, {withCredentials: true})
  }

  // Imports/exports templates
  importTemplates(sectionId: number, templateIds: number[]) {
    return this.http.post(`${apiUrl}/section/${sectionId}/templates/import`, templateIds, {withCredentials: true})
  }

  exportTemplates(projectId: number, templateIds: number[]) {
    return this.http.post(`${apiUrl}/project/${projectId}/templates/export`, templateIds, {withCredentials: true})
  }

  // Imports trades
  importTrades(projectId: number, tradeIds: number[]) {
    return this.http.post(`${apiUrl}/project/${projectId}/trades`, tradeIds, {withCredentials: true})
  }

  getAvailableTradesShortList(projectId: number) {
    return this.http.get<Trade[]>(`${apiUrl}/project/${projectId}/trades/available`, {withCredentials: true})
  }

  removeTrades(projectId: number, tradeIds: number[]) {
    return this.http.delete(`${apiUrl}/project/${projectId}/trades`, {params: {tradeIds}, withCredentials: true})
  }

}
